import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftTypography from "components/SoftTypography";
import { AxiosError } from "axios";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

// Type definitions
type DateInput = string | number | Date;

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

interface RowType {
  id: number;
  status: string;
  ticket_id: number | null;
  asset_service_id: string;
  note: string | null;
  customer_id: number;
  asset_id: number;
  asset_type_id: number;
  address_id: number;
  division_id: number | null;
}

// Utility function moved outside component
const formatDate = (dateString: DateInput) => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  };
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", options).format(date);
};

// Get button text helper function
const getButtonText = (hasTicket: boolean, isLoading: boolean): string => {
  if (hasTicket) {
    return "Service Created";
  }
  if (isLoading) {
    return "Creating...";
  }
  return "Create Free-Service";
};

// Extracted Actions Cell Component
interface ActionsCellProps {
  row: RowType;
  loadingId: number | null;
  onCreateTicket: (row: RowType) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  row,
  loadingId,
  onCreateTicket,
}) => {
  const isLoading = loadingId === row.id;
  const hasTicket = Boolean(row.ticket_id);
  const buttonText = getButtonText(hasTicket, isLoading);

  return (
    <Button
      size="small"
      variant="contained"
      disabled={isLoading ?? hasTicket}
      color={hasTicket ? "success" : "primary"}
      onClick={() => onCreateTicket(row)}
      startIcon={isLoading && <CircularProgress size={16} />}
    >
      {buttonText}
    </Button>
  );
};

const UpcomingFreeService = () => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [rows, setRows] = React.useState<RowType[]>([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeItem[] | null>(
    null
  );
  const [loadingId, setLoadingId] = React.useState<number | null>(null);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);

  const createFreeServiceTicket = React.useCallback(
    async (row: RowType) => {
      try {
        setLoadingId(row.id);

        const ticketData = {
          title: `Free Service - ${row.asset_service_id}`,
          description: row.note ?? "Free service auto-generated",
          customer_id: row.customer_id,
          asset_id: row.asset_id,
          asset_type_id: row.asset_type_id,
          status_id: 1,
          address_id: row.address_id,
          category_id: null,
          division_id: row.division_id ?? null,
          status_history: JSON.stringify([
            {
              updatedDate: new Date().toISOString(),
              status: "open",
              changedBy: "System",
              message: "Free service ticket auto-created",
            },
          ]),
        };

        const ticketResponse = await axios.post("/tickets", { ticketData });
        const ticketId = ticketResponse.data.id;

        await axios.put(`/subscription/free-services/${row.id}`, {
          ticket_id: ticketId,
          status: "claimed",
        });

        fetchSuccess("Ticket Created Successfully!");

        setRows((prev) =>
          prev.map((item) =>
            item.id === row.id
              ? { ...item, status: "claimed", ticket_id: ticketId }
              : item
          )
        );
      } catch (error) {
        console.error(error);
        fetchError("Failed to create ticket");
      } finally {
        setLoadingId(null);
      }
    },
    [fetchSuccess, fetchError]
  );

  // Render function for actions cell
  const renderActionsCell = React.useCallback(
    (value: any, tableMeta: any) => {
      const row = tableMeta.tableData[tableMeta.rowIndex];
      return (
        <ActionsCell
          row={row}
          loadingId={loadingId}
          onCreateTicket={createFreeServiceTicket}
        />
      );
    },
    [loadingId, createFreeServiceTicket]
  );

  // Render function for date cell
  const renderDateCell = React.useCallback((value: DateInput) => {
    return formatDate(value);
  }, []);

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      { name: "name", label: "Customer" },
      { name: "asset_service_id", label: "Asset Id" },
      { name: "asset_type_name", label: "Asset Type" },
      { name: "subscription_type", label: "Subscription Type" },
      {
        name: "scheduled_at",
        label: "Scheduled Date",
        options: {
          filter: false,
          customBodyRender: renderDateCell,
        },
      },
      {
        name: "status",
        label: "Status",
      },
      {
        name: "id",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRender: renderActionsCell,
        },
      },
    ],
    [renderDateCell, renderActionsCell]
  );

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/subscription/free-services", {
        params: {
          filters: {
            asset_type_id: assetTypeFilter?.asset_type_id,
          },
        },
      });

      const today = new Date();
      const minus7 = new Date();
      minus7.setDate(today.getDate() - 7);
      const plus7 = new Date();
      plus7.setDate(today.getDate() + 7);

      // Filter rows based on scheduled_at
      const filteredRows = response?.data?.filter(
        (item: { scheduled_at: DateInput }) => {
          const scheduledDate = new Date(item.scheduled_at);
          return scheduledDate >= minus7 && scheduledDate <= plus7;
        }
      );
      setRows(filteredRows);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, [assetTypeFilter]);

  React.useEffect(() => {
    fetchData();
  }, [assetTypeFilter, fetchData]);

  const fetchAssets = React.useCallback(async () => {
    try {
      const assetTypesData = await axios.get("/asset-types");
      setAssetTypes(assetTypesData?.data?.list);
    } catch (error) {
      console.error("Error: ", error);
    }
  }, []);

  React.useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <FormControl sx={{ marginBottom: "3px" }}>
            <SoftTypography
              variant="caption"
              fontWeight="bold"
              sx={{ paddingBottom: "10px" }}
            >
              Asset Types
            </SoftTypography>
            <Autocomplete
              options={assetTypes ?? []}
              getOptionLabel={(option) => option.asset_type_name}
              value={assetTypeFilter}
              onChange={(event, value) => {
                setAssetTypeFilter(value);
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
          </FormControl>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-subscriptions"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Free Services"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
    </DashboardLayout>
  );
};

export default UpcomingFreeService;
